import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { 
  Sparkles, Send, ArrowLeft, Loader2, User, Bot, 
  Lightbulb, DollarSign, Users, Briefcase, TrendingUp,
  Clock, FileText, RefreshCw, Mic, MicOff, Volume2, ShoppingCart,
  Mail, ChevronRight, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { startRecording as startVoiceRecording } from '../lib/voiceRecorder';
import { useAICreditGuard } from '../components/credits/AICreditConfirmationDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Shared readable error helper - prevents "Error: [object Object]"
function getReadableError(error) {
  if (!error) return "Something went wrong.";
  if (typeof error === "string") return error;
  if (error.message) return error.message;
  if (error.response?.data?.detail) return error.response.data.detail;
  if (error.response?.data?.message) return error.response.data.message;
  if (error.data?.detail) return error.data.detail;
  if (error.data?.message) return error.data.message;
  try {
    const str = JSON.stringify(error);
    if (str !== '{}') return str;
  } catch {
    // ignore
  }
  return "Something went wrong.";
}

const suggestedPrompts = [
  { icon: DollarSign, text: "What's a good profit margin for vehicle wraps?", category: 'pricing' },
  { icon: Users, text: "How do I handle a difficult customer complaint?", category: 'customers' },
  { icon: Briefcase, text: "What questions should I ask for a sign project?", category: 'sales' },
  { icon: TrendingUp, text: "How can I increase my average order value?", category: 'growth' },
  { icon: Clock, text: "How long should a full vehicle wrap take?", category: 'operations' },
  { icon: FileText, text: "Write a follow-up email for a quote I sent", category: 'communications' },
  { icon: ShoppingCart, text: "Make an order for a customer", category: 'orders' },
];

/**
 * Inline confirmation pill rendered below an assistant reply. Handles every
 * action_type produced by the backend tool router (pass-3): navigate,
 * create_task, create_appointment, metric, draft_email, send_invoice.
 *
 * For `navigate` and `metric`, we auto-execute or show a passive label —
 * no confirm needed. For `create_task` and `create_appointment` we surface
 * an editable summary + a one-click "Add" button.
 */
function ProposedActionPill({ action, onRun }) {
  if (!action) return null;

  if (action.status === 'needs_clarification') {
    return (
      <div
        className="mt-3 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-3 py-2"
        data-testid="proposed-action-needs-clarification"
      >
        {action.hint || 'Need a bit more info.'}
      </div>
    );
  }

  if (action.status === 'completed') {
    return (
      <div className="mt-3 text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md px-3 py-2 flex items-center gap-2">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Done.
      </div>
    );
  }

  // Metric — answer is in the chat text already. Show a small inline chip.
  if (action.action_type === 'metric') {
    return (
      <div
        className="mt-3 inline-flex items-center gap-1.5 bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded"
        data-testid="proposed-action-metric"
      >
        <Sparkles className="h-3 w-3" />
        {action.value_label}
      </div>
    );
  }

  // Navigate — show an "Opening …" hint (the auto-execute path is handled in
  // the chat container via a useEffect; we keep the pill as a fallback).
  if (action.action_type === 'navigate') {
    return (
      <div
        className="mt-3 flex items-center justify-between gap-3 bg-blue-50 border border-blue-200 rounded-md px-3 py-2"
        data-testid="proposed-action-navigate"
      >
        <span className="text-sm text-blue-900">→ {action.label}</span>
        <Button
          size="sm"
          onClick={() => onRun(action)}
          variant="outline"
          className="text-blue-700 border-blue-300 h-7 text-xs"
          data-testid="proposed-action-navigate-go"
        >
          Open
        </Button>
      </div>
    );
  }

  if (action.action_type === 'create_task') {
    return (
      <div
        className="mt-3 flex items-center justify-between gap-3 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2"
        data-testid="proposed-action-create-task"
      >
        <div className="min-w-0 text-sm text-emerald-900">
          <div className="font-medium truncate">{action.title}</div>
          {action.due_date && (
            <div className="text-xs text-emerald-700">Due {action.due_date.slice(0, 10)}</div>
          )}
        </div>
        <Button
          size="sm"
          onClick={() => onRun(action)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3 text-xs shrink-0"
          data-testid="proposed-action-create-task-confirm"
        >
          Add task <ChevronRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    );
  }

  if (action.action_type === 'create_appointment') {
    return (
      <div
        className="mt-3 flex items-center justify-between gap-3 bg-cyan-50 border border-cyan-200 rounded-md px-3 py-2"
        data-testid="proposed-action-create-appointment"
      >
        <div className="min-w-0 text-sm text-cyan-900">
          <div className="font-medium truncate">{action.title}</div>
          <div className="text-xs text-cyan-700">
            {action.start_at?.slice(0, 16).replace('T', ' ')} · {action.duration_minutes || 60} min
            {action.customer?.name && ` · ${action.customer.name}`}
          </div>
        </div>
        <Button
          size="sm"
          onClick={() => onRun(action)}
          className="bg-cyan-600 hover:bg-cyan-700 text-white h-8 px-3 text-xs shrink-0"
          data-testid="proposed-action-create-appointment-confirm"
        >
          Schedule <ChevronRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    );
  }

  if (action.action_type === 'set_reminder') {
    return (
      <div
        className="mt-3 flex items-center justify-between gap-3 bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2"
        data-testid="proposed-action-set-reminder"
      >
        <div className="min-w-0 text-sm text-yellow-900">
          <div className="font-medium truncate">{action.text}</div>
          <div className="text-xs text-yellow-700">
            {action.remind_at?.slice(0, 16).replace('T', ' ')}
          </div>
        </div>
        <Button
          size="sm"
          onClick={() => onRun(action)}
          className="bg-yellow-600 hover:bg-yellow-700 text-white h-8 px-3 text-xs shrink-0"
          data-testid="proposed-action-set-reminder-confirm"
        >
          Set <ChevronRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    );
  }

  if (action.action_type === 'send_quote_followup_bulk') {
    return (
      <div
        className="mt-3 bg-rose-50 border border-rose-200 rounded-md px-3 py-2.5"
        data-testid="proposed-action-bulk-followup"
      >
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="text-sm text-rose-900 font-medium">
            Send {action.count} stale-quote follow-ups
          </div>
          <Button
            size="sm"
            onClick={() => onRun(action)}
            className="bg-rose-600 hover:bg-rose-700 text-white h-8 px-3 text-xs"
            data-testid="proposed-action-bulk-followup-confirm"
          >
            Send all <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
        <div className="space-y-0.5 text-xs text-rose-800">
          {(action.previews || []).slice(0, 5).map((p) => (
            <div key={p.quote_id} className="truncate">
              · {p.customer_name || 'Customer'} — #{p.quote_number || p.quote_id?.slice(0, 6)} · ${(p.total || 0).toFixed(2)}
            </div>
          ))}
          {(action.previews || []).length > 5 && (
            <div className="text-rose-600">… and {(action.previews || []).length - 5} more</div>
          )}
        </div>
      </div>
    );
  }

  if (action.action_type === 'find_customer') {
    const c = action.customer || {};
    const inv = action.recent_invoices || [];
    const ords = action.recent_orders || [];
    return (
      <div
        className="mt-3 bg-sky-50 border border-sky-200 rounded-md px-3 py-2.5"
        data-testid="proposed-action-find-customer"
      >
        <div className="flex items-center justify-between gap-3 mb-1.5">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-sky-900 truncate">{c.name || 'Customer'}</div>
            <div className="text-xs text-sky-700 truncate">
              {[c.email, c.company].filter(Boolean).join(' · ') || 'No contact info on file'}
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => onRun(action)}
            className="bg-sky-600 hover:bg-sky-700 text-white h-8 px-3 text-xs shrink-0"
            data-testid="proposed-action-find-customer-confirm"
          >
            Open <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
        {(inv.length > 0 || ords.length > 0) && (
          <div className="text-xs text-sky-800 space-y-0.5">
            {inv.slice(0, 3).map((i) => (
              <div key={`inv_${i.id}`} className="truncate">
                · Invoice #{i.invoice_number || i.id?.slice(0, 6)} — {i.status} · ${((i.grand_total || i.total || 0)).toFixed(2)}
              </div>
            ))}
            {ords.slice(0, 3).map((o) => (
              <div key={`job_${o.id}`} className="truncate">
                · {o.title || 'Order'} — {o.status}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (action.action_type === 'attach_note_to_customer') {
    return (
      <div
        className="mt-3 flex items-center justify-between gap-3 bg-indigo-50 border border-indigo-200 rounded-md px-3 py-2"
        data-testid="proposed-action-attach-note"
      >
        <div className="min-w-0 text-sm text-indigo-900">
          <div className="font-medium truncate">
            Add note to {action.customer?.name || 'customer'}
          </div>
          <div className="text-xs text-indigo-700 truncate">"{action.note}"</div>
        </div>
        <Button
          size="sm"
          onClick={() => onRun(action)}
          className="bg-indigo-600 hover:bg-indigo-700 text-white h-8 px-3 text-xs shrink-0"
          data-testid="proposed-action-attach-note-confirm"
        >
          Add <ChevronRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    );
  }

  // Email / invoice (pass-1/2)
  const Icon = action.action_type === 'send_invoice' ? FileText : Mail;
  return (
    <div
      className="mt-3 flex items-center justify-between gap-3 bg-purple-50 border border-purple-200 rounded-md px-3 py-2"
      data-testid={`proposed-action-${action.action_type}`}
    >
      <div className="flex items-center gap-2 text-sm text-purple-900 min-w-0">
        <Icon className="h-4 w-4 flex-shrink-0 text-purple-600" />
        <div className="min-w-0">
          <div className="font-medium truncate">
            {action.confirm_label || 'Run action'} → {action.customer?.name}
          </div>
          {action.customer?.email && (
            <div className="text-xs text-purple-700 truncate">{action.customer.email}</div>
          )}
        </div>
      </div>
      <Button
        size="sm"
        onClick={() => onRun(action)}
        className="bg-purple-600 hover:bg-purple-700 text-white h-8 px-3 text-xs shrink-0"
        data-testid={`proposed-action-${action.action_type}-confirm`}
      >
        Open <ChevronRight className="h-3.5 w-3.5 ml-1" />
      </Button>
    </div>
  );
}

export default function AIAssistant() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { runGuardedAction, dialog: creditDialog } = useAICreditGuard();
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Hey there! I'm your AI Business Assistant for sign shop operations. I can help you with:

- **Pricing & Quoting** - Profit margins, competitive pricing, upselling
- **Customer Management** - Handling complaints, communication tips
- **Operations** - Production times, workflow optimization
- **Sales & Growth** - Closing deals, marketing ideas, increasing revenue
- **Create Orders** - Say "Make an order for [customer] for [product]" and I'll help you build it step by step

What can I help you with today?`
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [activeOrderDraft, setActiveOrderDraft] = useState(null);
  const [sessionId] = useState(() => `assistant_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  // Voice state: 'idle' | 'listening' | 'transcribing' | 'thinking' | 'speaking' | 'error'
  const [voiceState, setVoiceState] = useState('idle');
  const [voiceTranscript, setVoiceTranscript] = useState(null); // Show transcript immediately
  const [voiceError, setVoiceError] = useState(null);
  const [voiceTimings, setVoiceTimings] = useState(null); // Debug timing logs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const voiceAbortRef = useRef(null); // For timeout handling

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    return () => {
      streamRef.current?.getTracks?.().forEach((track) => track.stop());
    };
  }, []);

  const playVoice = async (text) => {
    if (!text?.trim()) return;
    
    // Prevent duplicate TTS while already speaking
    if (voiceState === 'speaking') return;
    
    await runGuardedAction({
      actionType: 'voice_tts',
      featureName: 'Business Assistant Voice Output',
      execute: async () => {
        setVoiceLoading(true);
        setVoiceState('speaking');
        const ttsStart = Date.now();
        
        try {
          const response = await axios.post(
            `${API_URL}/api/ai/voice/speak`,
            { text, voice: 'alloy', speed: 1.0 },
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              timeout: 30000 // 30 second timeout for TTS
            }
          );
          
          const ttsTime = Date.now() - ttsStart;
          console.log(`[Voice] TTS completed in ${ttsTime}ms`);
          
          const audio = new Audio(`data:${response.data.mime_type};base64,${response.data.audio_base64}`);
          
          // Wait for audio to finish playing
          await new Promise((resolve, reject) => {
            audio.onended = resolve;
            audio.onerror = reject;
            audio.play().catch(reject);
          });
          
          return response.data;
        } catch (error) {
          console.error('Voice output error:', error);
          // TTS failure should not block - just show the text response
          toast.error('Voice playback failed, but you can read the response above.');
          // Don't re-throw - allow the flow to continue
        } finally {
          setVoiceLoading(false);
          setVoiceState('idle');
        }
      }
    });
  };

  // Holds the active recording handle returned by startVoiceRecording().
  // .stop() returns { blob, mimeType, filename }.
  const voiceHandleRef = useRef(null);

  const stopRecording = async () => {
    const handle = voiceHandleRef.current;
    if (!handle) return null;
    voiceHandleRef.current = null;
    streamRef.current = null;
    setIsRecording(false);
    try {
      const { blob, mimeType, filename } = await handle.stop();
      return { blob, mimeType, filename };
    } catch (err) {
      console.warn('stopRecording failed', err);
      return null;
    }
  };

  const handleVoiceInput = async () => {
    // Prevent duplicate requests while processing
    if (voiceState === 'transcribing' || voiceState === 'thinking' || voiceState === 'speaking') {
      return;
    }

    if (isRecording) {
      const recordingStopTime = Date.now();
      const result = await stopRecording();
      if (!result) return;
      const { blob: audioBlob, filename: audioFilename } = result;

      // Reset previous state
      setVoiceTranscript(null);
      setVoiceError(null);
      setVoiceTimings(null);

      const timings = {
        recordingDuration: 0,
        uploadStart: Date.now(),
        transcriptionTime: 0,
        aiResponseTime: 0,
        ttsTime: 0,
        totalTime: 0
      };

      await runGuardedAction({
        actionType: 'voice_transcription',
        featureName: 'Business Assistant Voice Input',
        execute: async () => {
          setVoiceLoading(true);
          setVoiceState('transcribing');
          
          try {
            // Create abort controller for timeout
            const controller = new AbortController();
            voiceAbortRef.current = controller;
            
            // 15 second timeout for transcription
            const transcriptionTimeout = setTimeout(() => {
              controller.abort();
            }, 15000);

            const formData = new FormData();
            formData.append('audio', audioBlob, audioFilename || 'assistant-input.webm');
            
            const transcribeStart = Date.now();
            const response = await axios.post(`${API_URL}/api/ai/voice/transcribe`, formData, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              },
              signal: controller.signal
            });
            
            clearTimeout(transcriptionTimeout);
            timings.transcriptionTime = Date.now() - transcribeStart;
            
            if (response.data?.text) {
              const transcript = response.data.text;
              setVoiceTranscript(transcript);
              setInput(transcript);
              
              // Log timing for debugging
              console.log(`[Voice] Transcription completed in ${timings.transcriptionTime}ms`);
              
              toast.success('Voice captured! You can edit or send as-is.');
            } else {
              throw new Error('No transcription returned');
            }
            
            setVoiceState('idle');
            setVoiceTimings(timings);
            return response.data;
            
          } catch (error) {
            console.error('Voice transcription error:', error);
            
            if (error.name === 'AbortError' || error.code === 'ECONNABORTED') {
              setVoiceError('Transcription timed out. Please try a shorter recording.');
              toast.error('Transcription timed out after 15 seconds. Try a shorter recording.');
            } else {
              const errorMsg = getReadableError(error);
              setVoiceError(errorMsg);
              toast.error(errorMsg);
            }
            
            setVoiceState('error');
            throw error;
          } finally {
            setVoiceLoading(false);
            voiceAbortRef.current = null;
          }
        }
      });
      return;
    }

    // Start recording (with voice-activity-detection auto-stop).
    try {
      const handle = await startVoiceRecording({
        onSilence: () => {
          // Auto-stop fires when the user has been silent for ~1.2s. We
          // re-enter the same handler in "stop" mode so the existing
          // transcription pipeline runs unchanged.
          // NB: we don't await — fire-and-forget.
          handleVoiceInputRef.current?.();
        },
      });
      voiceHandleRef.current = handle;
      streamRef.current = handle.stream;
      setIsRecording(true);
      setVoiceState('listening');
      setVoiceTranscript(null);
      setVoiceError(null);
      toast.info('Recording... I\'ll auto-stop when you finish speaking.');
    } catch (error) {
      console.error('Microphone access error:', error);
      setVoiceState('error');
      setVoiceError('Microphone access failed');
      toast.error(getReadableError(error) || 'Microphone access failed. Please allow microphone access and try again.');
    }
  };

  // Stable ref so VAD's onSilence callback always sees the latest function.
  const handleVoiceInputRef = useRef(null);
  useEffect(() => {
    handleVoiceInputRef.current = handleVoiceInput;
  });

  const handleSend = async (messageText = input) => {
    if (!messageText.trim() || loading) return;

    const userMessage = { role: 'user', content: messageText.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setVoiceTranscript(null); // Clear transcript after sending
    setLoading(true);

    try {
      await runGuardedAction({
        actionType: 'ai_business_assistant',
        featureName: 'AI Business Assistant',
        execute: async () => {
          const aiStart = Date.now();
          
          // Create abort controller for 20 second timeout
          const controller = new AbortController();
          const aiTimeout = setTimeout(() => {
            controller.abort();
          }, 20000);
          
          try {
            const response = await axios.post(
              `${API_URL}/api/ai/assistant`,
              {
                message: messageText.trim(),
                session_id: sessionId,
                conversation_history: messages.slice(-30)
              },
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                signal: controller.signal
              }
            );
            
            clearTimeout(aiTimeout);
            const aiTime = Date.now() - aiStart;
            console.log(`[Assistant] AI response in ${aiTime}ms`);

            const assistantMessage = {
              role: 'assistant',
              content: response.data.response,
              proposed_action: response.data.proposed_action || null,
            };
            setMessages(prev => [...prev, assistantMessage]);
            
            // Update active order draft if returned from backend
            if (response.data.active_order_draft) {
              setActiveOrderDraft(response.data.active_order_draft);
            }
            
            return assistantMessage;
          } catch (error) {
            clearTimeout(aiTimeout);
            if (error.name === 'AbortError' || error.code === 'ECONNABORTED') {
              throw new Error('Response timed out after 20 seconds. Please try again.');
            }
            throw error;
          }
        }
      });
    } catch (error) {
      console.error('Assistant error:', error);
      toast.error(getReadableError(error));
      // Remove the user message if we failed
      setMessages(prev => prev.slice(0, -1));
      setInput(messageText);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestedPrompt = (prompt) => {
    handleSend(prompt);
  };

  /**
   * Execute a proposed action from the assistant. New action types added in
   * pass 3: navigate (auto-routes), create_task / create_appointment (show
   * confirm pill then commit via /assistant/commit-*), metric (no-op,
   * answer is in the chat reply already).
   */
  const handleRunProposedAction = async (action) => {
    if (!action || action.status !== 'ready') return;

    // Navigate — auto-execute
    if (action.action_type === 'navigate' && action.path) {
      navigate(action.path);
      return;
    }

    // Metric — answer already in reply; nothing to do
    if (action.action_type === 'metric') {
      return;
    }

    // Create task — commit via API
    if (action.action_type === 'create_task') {
      try {
        await api.post('/ai/assistant/commit-task', {
          title: action.title,
          due_date: action.due_date,
          priority: action.priority,
        });
        toast.success('Task added');
        action.status = 'completed';  // local — pill re-renders disabled
        setMessages((prev) => [...prev]);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to add task');
      }
      return;
    }

    // Create appointment — commit via API
    if (action.action_type === 'create_appointment') {
      try {
        await api.post('/ai/assistant/commit-appointment', {
          title: action.title,
          start_at: action.start_at,
          duration_minutes: action.duration_minutes,
          customer: action.customer,
        });
        toast.success('Appointment added to your calendar');
        action.status = 'completed';
        setMessages((prev) => [...prev]);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to schedule');
      }
      return;
    }

    // Reminder — commit via API
    if (action.action_type === 'set_reminder') {
      try {
        await api.post('/ai/assistant/commit-reminder', {
          text: action.text,
          remind_at: action.remind_at,
        });
        toast.success('Reminder set');
        action.status = 'completed';
        setMessages((prev) => [...prev]);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to set reminder');
      }
      return;
    }

    // Bulk quote follow-up — fire SendGrid for the proposed quote list
    if (action.action_type === 'send_quote_followup_bulk') {
      try {
        const { data } = await api.post('/ai/assistant/bulk-followup-quotes', {
          previews: action.previews,
        });
        toast.success(`Sent ${data?.sent || 0} follow-up emails`);
        action.status = 'completed';
        setMessages((prev) => [...prev]);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Bulk follow-up failed');
      }
      return;
    }

    // Find customer — navigate to the customer detail page
    if (action.action_type === 'find_customer') {
      const cid = action.customer?.id;
      if (!cid) {
        toast.error('No customer to open');
        return;
      }
      navigate(`/customers/${cid}`);
      return;
    }

    // Attach note to customer — commit via API
    if (action.action_type === 'attach_note_to_customer') {
      const cid = action.customer?.id;
      if (!cid || !action.note) {
        toast.error('Missing customer or note');
        return;
      }
      try {
        await api.post('/ai/assistant/commit-note-to-customer', {
          customer: action.customer,
          note: action.note,
        });
        toast.success(`Note added to ${action.customer?.name || 'customer'}`);
        action.status = 'completed';
        setMessages((prev) => [...prev]);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to add note');
      }
      return;
    }

    // Email / invoice actions (existing pass-1/2 behavior)
    const cid = action.customer?.id;
    if (!cid) {
      toast.error('No customer linked to this action');
      return;
    }
    if (action.action_type === 'draft_email') {
      const subject = action.about ? `RE: ${action.about}` : 'Quick note';
      navigate(`/customers/${cid}?compose_email=1&subject=${encodeURIComponent(subject)}`);
      return;
    }
    if (action.action_type === 'send_invoice') {
      navigate(`/billing/invoices/new?customer_id=${cid}`);
      return;
    }
    toast.info('Action queued — opening the right page.');
  };

  const handleNewChat = async () => {
    // Clear the persistent server-side conversation so the assistant forgets
    // what was said across reloads too (not just this tab).
    try {
      await axios.delete(`${API_URL}/api/ai/assistant/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      console.warn('Assistant clear failed', err);
    }
    setMessages([{
      role: 'assistant',
      content: `Hey there! I'm your AI Business Assistant for sign shop operations. I can help you with:

- **Pricing & Quoting** - Profit margins, competitive pricing, upselling
- **Customer Management** - Handling complaints, communication tips
- **Operations** - Production times, workflow optimization
- **Sales & Growth** - Closing deals, marketing ideas, increasing revenue
- **Create Orders** - Say "Make an order for [customer] for [product]" and I'll help you build it step by step

What can I help you with today?`
    }]);
    setActiveOrderDraft(null); // Clear the order draft when starting new chat
    setVoiceState('idle');
    setVoiceTranscript(null);
    setVoiceError(null);
  };

  // Hydrate persistent conversation history on mount so the assistant
  // remembers what was said across page reloads / navigation.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/ai/assistant/history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const saved = r.data?.messages || [];
        if (!cancelled && saved.length > 0) {
          // Replace the greeting with the persisted conversation.
          setMessages(saved.map((m) => ({ role: m.role, content: m.content })));
        }
      } catch (err) {
        console.warn('Assistant history hydrate failed', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {creditDialog}
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/ai-tools')}
            className="text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-purple-500" />
              AI Business Assistant
            </h1>
            <p className="text-sm text-gray-500">Your sign shop operations expert</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleNewChat}
          className="flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          New Chat
        </Button>
      </div>

      {/* Chat Area */}
      <Card className="flex-1 flex flex-col overflow-hidden">
        {/* Compact Order Draft Indicator */}
        {activeOrderDraft && activeOrderDraft.intent === 'create_order' && (
          <div className="px-4 py-2 bg-green-50 border-b border-green-200 flex items-center gap-3" data-testid="active-order-draft-indicator">
            <ShoppingCart className="h-4 w-4 text-green-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-xs font-medium text-green-800">Creating Order</span>
              <span className="text-xs text-green-600 ml-2">
                {activeOrderDraft.customer_name && `Customer: ${activeOrderDraft.customer_name}`}
                {activeOrderDraft.order_items?.[0]?.product_type && ` • ${activeOrderDraft.order_items[0].product_type}`}
                {activeOrderDraft.order_items?.[0]?.quantity && ` • Qty: ${activeOrderDraft.order_items[0].quantity}`}
                {activeOrderDraft.order_items?.[0]?.size && ` • ${activeOrderDraft.order_items[0].size}`}
                {activeOrderDraft.order_items?.[0]?.material && ` • ${activeOrderDraft.order_items[0].material}`}
              </span>
            </div>
          </div>
        )}
        
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-4 max-w-3xl mx-auto">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {message.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Bot className="h-5 w-5 text-purple-600" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  <div className="whitespace-pre-wrap text-sm leading-relaxed prose prose-sm max-w-none">
                    {message.content.split('\n').map((line, i) => {
                      // Handle bold text
                      const parts = line.split(/(\*\*.*?\*\*)/g);
                      return (
                        <p key={i} className={i > 0 ? 'mt-2' : ''}>
                          {parts.map((part, j) => {
                            if (part.startsWith('**') && part.endsWith('**')) {
                              return <strong key={j}>{part.slice(2, -2)}</strong>;
                            }
                            return part;
                          })}
                        </p>
                      );
                    })}
                  </div>
                  {message.proposed_action && (
                    <ProposedActionPill
                      action={message.proposed_action}
                      onRun={(a) => handleRunProposedAction(a)}
                    />
                  )}
                </div>
                {message.role === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <User className="h-5 w-5 text-blue-600" />
                  </div>
                )}
              </div>
            ))}
            
            {loading && (
              <div className="flex gap-3 justify-start">
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Bot className="h-5 w-5 text-purple-600" />
                </div>
                <div className="bg-gray-100 rounded-2xl px-4 py-3">
                  <Loader2 className="h-5 w-5 animate-spin text-purple-500" />
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Suggested Prompts - Show only when few messages */}
        {messages.length <= 2 && (
          <div className="px-4 py-3 border-t bg-gray-50/50">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="h-4 w-4 text-amber-500" />
              <span className="text-xs font-medium text-gray-500">Suggested questions</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {suggestedPrompts.map((prompt, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestedPrompt(prompt.text)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-xs text-gray-600 hover:bg-gray-100 hover:border-gray-300 transition-colors"
                >
                  <prompt.icon className="h-3 w-3" />
                  {prompt.text}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="p-4 border-t">
          {/* Voice State Indicator */}
          {voiceState !== 'idle' && (
            <div className="flex items-center justify-center gap-2 mb-3 text-sm" data-testid="voice-state-indicator">
              {voiceState === 'listening' && (
                <>
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                  </span>
                  <span className="text-red-600 font-medium">Listening...</span>
                </>
              )}
              {voiceState === 'transcribing' && (
                <>
                  <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                  <span className="text-blue-600">Transcribing audio...</span>
                </>
              )}
              {voiceState === 'thinking' && (
                <>
                  <Loader2 className="h-4 w-4 animate-spin text-purple-500" />
                  <span className="text-purple-600">Thinking...</span>
                </>
              )}
              {voiceState === 'speaking' && (
                <>
                  <Volume2 className="h-4 w-4 text-green-500 animate-pulse" />
                  <span className="text-green-600">Speaking...</span>
                </>
              )}
              {voiceState === 'error' && voiceError && (
                <>
                  <span className="text-red-500">⚠️ {voiceError}</span>
                </>
              )}
            </div>
          )}
          
          {/* Show transcript immediately when available */}
          {voiceTranscript && voiceState === 'idle' && (
            <div className="flex items-center gap-2 mb-2 px-2 py-1 bg-blue-50 rounded text-sm text-blue-700">
              <Mic className="h-3 w-3" />
              <span>Heard: "{voiceTranscript}"</span>
            </div>
          )}
          
          <div className="flex gap-2 max-w-3xl mx-auto">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask me anything about running your sign shop..."
              className="min-h-[44px] max-h-[120px] resize-none"
              rows={1}
              disabled={loading || voiceState === 'transcribing'}
            />
          <Button
            type="button"
            variant="outline"
            onClick={handleVoiceInput}
            disabled={voiceLoading || loading || voiceState === 'transcribing' || voiceState === 'speaking'}
            className={isRecording ? 'border-red-400 text-red-600 bg-red-50' : ''}
            data-testid="assistant-voice-input-button"
            title={isRecording ? 'Click to stop recording' : 'Click to start voice input'}
          >
            {voiceLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : isRecording ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
          </Button>
            <Button
              onClick={() => handleSend()}
              disabled={!input.trim() || loading || voiceState === 'transcribing'}
              className="bg-purple-500 hover:bg-purple-600 px-4"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </div>
          <div className="flex items-center justify-center mt-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                const lastAssistant = [...messages].reverse().find((msg) => msg.role === 'assistant');
                if (lastAssistant) playVoice(lastAssistant.content);
              }}
              disabled={voiceLoading || !messages.some((msg) => msg.role === 'assistant')}
              data-testid="assistant-voice-output-button"
            >
              {voiceLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Volume2 className="h-4 w-4 mr-2" />}
              Read last reply aloud
            </Button>
          </div>
          <p className="text-xs text-gray-400 text-center mt-2">
            AI responses are suggestions only. Always verify important business decisions. Voice input and voice output are available in this assistant.
          </p>
        </div>
      </Card>
    </div>
  );
}
